import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration.js';
import { ComponentList } from '../../components/core/component-list.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntryStatus } from '../calendar-entry.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { TimeZone } from '../../time/timezone.js';
import { VersionRange } from '../core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { OpenReservationBase } from '../open-reservation-base.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { DayOfYear } from './day-of-year.js';
import { Service } from './service.js';
import { StaffProfile } from './staff-profile.js';
import { TimeOfDay } from './time-of-day.js';
import { QueryableOptionalString } from '../core/optional.js';
import { MonthOfYear } from './month-of-year.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';

export class OpenReservation extends OpenReservationBase {
  static readonly TYPE_ID: string = 'fd7e0334304a4193aa11b366020a6019';
  static readonly SCHEMA_VERSION: number = 4;

  public readonly dayId: string;
  public readonly monthId: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly status: CalendarEntryStatus,
    public readonly creatorId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly dayOfYear: DayOfYear,
    public readonly timeOfDay: TimeOfDay,
    public readonly timezone: TimeZoneRegion,
    public readonly creationDate: dayjs.Dayjs,
    public readonly staffMemberId: QueryableOptionalString,
    public readonly staffMember: StaffProfile | null,
    public readonly groupId: string | null,
    public readonly components: ComponentList,
    public readonly duration: Duration,
    public readonly serviceId: string,
    public readonly service: Service,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
  ) {
    super();
    this.dayId = dayOfYear.uniqueId;
    this.monthId = MonthOfYear.dayOfYearToUniqueId(dayOfYear);
  }

  get parentId(): QueryableOptionalString {
    return QueryableOptionalString.none;
  }

  get clientOrderGroupId(): string {
    return this.uniqueId;
  }

  get $typeId(): string {
    return OpenReservation.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(2, OpenReservation.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenReservation {
    return new OpenReservation(
        MapUtils.get<string>(map, 'uniqueId'),
        toEnumFromString(MapUtils.get<CalendarEntryStatus>(map, '$status'), CalendarEntryStatus),
        MapUtils.get<string>(map, 'creatorId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.fromData<DayOfYear>(MapUtils.getRaw(map, 'dayOfYear') as SerializedData),
        context.fromData<TimeOfDay>(MapUtils.getRaw(map, 'timeOfDay') as SerializedData),
        MapUtils.get<TimeZoneRegion>(map, 'timezone'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        new QueryableOptionalString(MapUtils.getOrNull<string>(map, 'staffMemberId')),
        context.fromDataOrNull<StaffProfile>(MapUtils.getOrNull<SerializedData>(map, 'staffMember')),
        MapUtils.getOrNull<string>(map, 'groupId'),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(
                map,
                'components',
                (data) => data as SerializedData,
            ),
        ),
        context.decodeDuration(MapUtils.get<number>(map, 'duration')),
        MapUtils.get<string>(map, 'serviceId'),
        context.fromData<Service>(MapUtils.getRaw(map, 'service') as SerializedData),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'cancelledDate')),
        MapUtils.getOrNull<string>(map, 'cancelledBy'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'businessRef': this.businessRef.encode(),
      'dayId': this.dayId,
      'monthId': this.monthId,
      'dayOfYear': context.toData(this.dayOfYear),
      'timeOfDay': context.toData(this.timeOfDay),
      'timezone': this.timezone,
      'creationDate': context.encodeDate(this.creationDate),
      'date': context.encodeDate(this.time),
      'serviceId': this.serviceId,
      'service': context.toData(this.service),
      'staffMemberId': this.staffMemberId.queryableValue,
      'staffMember': context.toDataOrNull(this.staffMember),
      '$status': fromEnumAsString(this.status),
      'groupId': this.groupId,
      'parentId': this.parentId.queryableValue,
      'clientOrderGroupId': this.clientOrderGroupId,
      'duration': context.encodeDuration(this.duration),
      'components': this.components.serialize(context),
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
    };
  }

  clone(): this {
    return new OpenReservation(
        this.uniqueId,
        this.status,
        this.creatorId,
        this.businessRef.clone(),
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.staffMemberId.clone(),
        this.staffMember?.clone() ?? null,
        this.groupId,
        this.components.clone(),
        this.duration,
        this.serviceId,
        this.service.clone(),
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }

  isSame(other: OpenReservation): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.status === this.status &&
        other.creatorId === this.creatorId &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.dayOfYear, this.dayOfYear) &&
        isSameData(other.timeOfDay, this.timeOfDay) &&
        other.timezone === this.timezone &&
        other.creationDate === this.creationDate &&
        isSameData(other.staffMemberId, this.staffMemberId) &&
        isSameData(other.staffMember, this.staffMember) &&
        other.groupId === this.groupId &&
        other.components.isSame(this.components) &&
        other.duration === this.duration &&
        other.serviceId === this.serviceId &&
        isSameData(other.service, this.service);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new OpenReservation(
        this.uniqueId,
        this.status,
        this.creatorId,
        businessRef.clone(),
        this.dayOfYear.clone(),
        this.timeOfDay.clone(),
        this.timezone,
        TimeZone.cloneDate(this.creationDate),
        this.staffMemberId,
        this.staffMember?.copyWithBusinessRef(businessRef) ?? null,
        this.groupId,
        this.components.copyWithBusinessRef(businessRef),
        this.duration,
        this.serviceId,
        this.service.copyWithBusinessRef(businessRef),
        TimeZone.cloneDateOrNull(this.cancelledDate),
        this.cancelledBy,
    ) as this;
  }
}

export class OpenReservationBuilder extends DataModelBuilder<OpenReservation> {
  build(context: SerializationContext, map: SerializedData): OpenReservation {
    return OpenReservation.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    MapUtils.replace(
        map, '$status', 'DELETED',
        fromEnumAsString(CalendarEntryStatus.Canceled),
    );

    if(versionRange.contains(OpenReservation.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenReservation.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version [businessId] was replaced with [businessRef]
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));
      version = 2;
    }

    // In this version the [dayId] field was removed from the constructor
    if(version < 3) {
      version = 3;
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 4) {
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
